import { MenuItem, Stack } from '@mui/material';
import dayjs from 'dayjs';
import { Controller, useForm } from 'react-hook-form';

import { isPositionEquals } from '@/domain/position-geocode/utils/is-position-equals';
import { editTourEvent } from '@/domain/project-tour-ontour/actions/edit-tour-event.action';
import { TourEvent } from '@/domain/project-tour-ontour/entities/project-tour-ontour-event.entity';
import { FSPrimaryButton } from '@/ui/components/button/FSPrimaryButton';
import { FSSecondaryButton } from '@/ui/components/button/FSSecondaryButton';
import { FSPositionGeocodeField } from '@/ui/components/input/FSPositionGeocodeField';
import { FSTextField } from '@/ui/components/input/FSTextField';
import { useAppDispatch } from '@/ui/hook/store';
import { translate } from '@/ui/i18n';
import { isNumber } from '@/ui/utils/form-validations';

import {
  TourEventFormValues,
  typeOptions,
} from '../tour-event-form-values/TourEventsFormValues';

export function TourEventView({
  tourEvent,
  onClose,
}: {
  tourEvent: TourEvent;
  onClose: () => void;
}) {
  const dispatch = useAppDispatch();

  const { handleSubmit, register, control, formState } =
    useForm<TourEventFormValues>({
      defaultValues: {
        name: tourEvent.name,
        date: tourEvent.date,
        position: tourEvent.position,
        capacity: tourEvent.capacity,
        type: tourEvent.type,
      },
    });

  const { errors } = formState;

  const onSubmit = async (data: TourEventFormValues) => {
    const positionHasChanged =
      isPositionEquals(data.position, tourEvent.position) === false;
    try {
      const params = {
        tourEvent: {
          ...tourEvent,
          name: data.name,
          date: dayjs(data.date).toISOString(),
          position: data.position,
          capacity: parseInt(data.capacity.toString()),
          type: data.type,
        },
        updatePosition: positionHasChanged,
      };
      await dispatch(editTourEvent(params)).unwrap();
      onClose();
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack>
        <FSTextField
          label={translate('ProjectsTour.AddTourEventsModal.event-name')}
          register={{ ...register('name'), required: true }}
          error={!!errors.name}
          helperText={errors.name?.message}
        />
        <Controller
          name="position"
          control={control}
          defaultValue={tourEvent.position}
          render={({ field }) => (
            <FSPositionGeocodeField
              placeholder={translate(
                'ProjectsTour.AddTourEventsModal.position-placeholder',
              )}
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />
        <FSTextField
          label={translate('ProjectsTour.AddTourEventsModal.attendance')}
          register={{
            ...register('capacity', {
              validate: isNumber,
            }),
          }}
          error={!!errors.capacity}
          helperText={errors.capacity?.message}
        />
        <Controller
          name={'type'}
          control={control}
          defaultValue={tourEvent.type}
          rules={{ required: true }}
          render={({ field }) => (
            <FSTextField
              select
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              error={!!errors.type}
            >
              {typeOptions.map((value) => (
                <MenuItem key={value} value={value}>
                  {translate(`TourEventTypes.${value}`)}
                </MenuItem>
              ))}
            </FSTextField>
          )}
        />
        <Stack
          sx={{ flexDirection: 'row', justifyContent: 'end', mt: 1, gap: 1 }}
        >
          <FSSecondaryButton onClick={onClose}>
            {translate('Form.cancel')}
          </FSSecondaryButton>
          <FSPrimaryButton type="submit">
            {translate('Form.save')}
          </FSPrimaryButton>
        </Stack>
      </Stack>
    </form>
  );
}
